import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { 
  Lock, 
  Users, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  BookOpen, 
  Eye,
  LogOut,
  RefreshCw,
  BarChart3,
  Library
} from 'lucide-react';

import { API } from '../config';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [seriesStats, setSeriesStats] = useState([]);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken') || '');

  useEffect(() => {
    if (adminToken) {
      verifyToken();
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setStats(response.data);
      setIsAuthenticated(true);
      fetchAllData();
    } catch (error) {
      localStorage.removeItem('adminToken');
      setAdminToken('');
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      const token = response.data.token;
      localStorage.setItem('adminToken', token);
      setAdminToken(token);
      setIsAuthenticated(true);
      fetchAllData();
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, subscribersRes, seriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/subscribers`, { headers }),
        axios.get(`${API}/admin/series-stats`, { headers })
      ]);

      setStats(statsRes.data);
      setSubscribers(subscribersRes.data.subscribers);
      setSeriesStats(seriesRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken('');
    setIsAuthenticated(false);
    setStats(null);
    setSubscribers([]);
    setSeriesStats([]);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-slate-800 border-slate-700">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-rose-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-slate-400">Letters of Unspoken Hearts</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Username</label>
              <Input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                placeholder="Enter admin username"
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                placeholder="Enter admin password"
                data-testid="admin-password-input"
              />
            </div>

            {loginError && (
              <p className="text-red-400 text-sm" data-testid="login-error">{loginError}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="admin-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-slate-400 hover:text-white text-sm transition-colors"
          >
            ← Back to main site
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-sm text-slate-400">Letters of Unspoken Hearts</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchAllData}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
            <Card className="p-6 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Total Users</p>
                  <p className="text-2xl font-bold text-white" data-testid="total-users">{stats.total_users}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Total Revenue</p>
                  <p className="text-2xl font-bold text-white" data-testid="total-revenue">${stats.total_revenue.toFixed(2)}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-rose-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-rose-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Active Subscribers</p>
                  <p className="text-2xl font-bold text-white" data-testid="active-subscribers">{stats.active_subscribers}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Letters Read</p>
                  <p className="text-2xl font-bold text-white" data-testid="letters-read">{stats.total_letters_read}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Secondary Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <p className="text-sm text-slate-400">Monthly Revenue</p>
              <p className="text-xl font-semibold text-green-400">${stats.monthly_revenue.toFixed(2)}</p>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <p className="text-sm text-slate-400">New This Month</p>
              <p className="text-xl font-semibold text-blue-400">{stats.new_subscribers_this_month}</p>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <p className="text-sm text-slate-400">Churn Rate</p>
              <p className="text-xl font-semibold text-amber-400">{stats.churn_rate}%</p>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <p className="text-sm text-slate-400">Active Readers</p>
              <p className="text-xl font-semibold text-rose-400">{stats.active_readers}</p>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="subscribers" className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger 
              value="subscribers" 
              className="data-[state=active]:bg-slate-700 text-slate-300 data-[state=active]:text-white"
              data-testid="subscribers-tab"
            >
              <Users className="w-4 h-4 mr-2" />
              Subscribers
            </TabsTrigger>
            <TabsTrigger 
              value="series" 
              className="data-[state=active]:bg-slate-700 text-slate-300 data-[state=active]:text-white"
              data-testid="series-tab"
            >
              <Library className="w-4 h-4 mr-2" />
              Series Analytics
            </TabsTrigger>
          </TabsList>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers">
            <Card className="bg-slate-800 border-slate-700 overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h2 className="text-lg font-semibold text-white">All Users</h2>
                <p className="text-sm text-slate-400">Manage and view user details</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="subscribers-table">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Letters Read</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {subscribers.map((sub) => (
                      <tr key={sub.user_id} className="hover:bg-slate-700/50" data-testid={`subscriber-row-${sub.user_id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-white">{sub.name}</p>
                            <p className="text-xs text-slate-400">{sub.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            sub.subscription_status === 'active' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-slate-600/50 text-slate-400'
                          }`}>
                            {sub.subscription_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-300">{sub.letters_read}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-400">
                            {new Date(sub.created_at).toLocaleDateString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Series Analytics Tab */}
          <TabsContent value="series">
            <div className="grid md:grid-cols-2 gap-6" data-testid="series-analytics">
              {seriesStats.map((series) => (
                <Card key={series.series_id} className="p-6 bg-slate-800 border-slate-700">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{series.title}</h3>
                      <p className="text-sm text-slate-400">{series.era}</p>
                    </div>
                    <div className="w-10 h-10 bg-rose-500/20 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-rose-400" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400">Total Letters</p>
                      <p className="text-xl font-bold text-white">{series.letter_count}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total Reads</p>
                      <p className="text-xl font-bold text-blue-400">{series.total_reads}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Unique Readers</p>
                      <p className="text-xl font-bold text-green-400">{series.unique_readers}</p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">Avg. Letters/Reader</span>
                      <span className="text-white font-medium">{series.avg_letters_read}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
